// components/Benchmark/MarketIntelligencePage.jsx
// SCM Market Intelligence — price history, inflation, should-cost and negotiation support
import React, { useMemo, useState } from "react";
import { Button, Field, Input, Select, Spinner, Text, Tooltip } from "@fluentui/react-components";
import {
  ArrowLeft24Regular, ArrowSync24Regular, Lightbulb24Regular,
  ArrowTrendingLines24Regular, Warning24Regular, CheckmarkCircle24Regular,
  Info24Regular, Calculator24Regular, People24Regular,
  DataBarVertical24Regular, Money24Regular, History24Regular,
  Box24Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useBenchmarkDB } from "../../hooks/useBenchmarkDB";
import { BENCHMARK_CATEGORIES } from "../../services/benchmarkService";
import {
  awardedRatesByCategoryYear, inflationByCategoryYear, costSavings,
  priceMovement, trackableCommodities, shouldCost, assessRate,
  supplierScorecard, budgetProjection, generateInsights,
  filterByDateRange, dateBounds, availableYears, productCatalogue,
  fmtPct, fmtSignedPct, fmtMoney, fmtCompact, usableRecords,
} from "../../services/priceIntelligenceService";
import ProductIntelligenceTab from "./ProductIntelligence";
import {
  GOLD, GOLD_LT, NAVY, GOOD, WARN, BAD, INK, MUTED, SERIES_COLORS, SEVERITY,
  Panel, KPI, Empty, ConfidenceChip, InsightList, LineChart,
  DateRangeControl, buildPresets, SearchableSelect, th, td,
} from "./intelUI";

// ── Tab 1: Insights briefing ──────────────────────────────────────────────────

function InsightsTab({ records }) {
  const insights = useMemo(() => generateInsights(records), [records]);
  const usable = usableRecords(records);
  const inflation = useMemo(() => inflationByCategoryYear(records), [records]);
  const savings = useMemo(() => costSavings(records), [records]);

  const latestYear = inflation.years[inflation.years.length - 1];
  const overallInf = latestYear ? inflation.overall[latestYear] : null;
  const totalDeclared = savings.byYear.reduce((s, y) => s + y.declared, 0);
  const totalDerived = savings.byYear.reduce((s, y) => s + y.derived, 0);
  const cur = usable[0]?.currency || "NGN";

  return (
    <>
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <KPI label="Records in evidence base" value={usable.length} sub={`${(records || []).length - usable.length} incomplete`} Icon={DataBarVertical24Regular} />
        <KPI label={`Portfolio inflation ${latestYear || ""}`} value={fmtSignedPct(overallInf)} sub="like-for-like, all categories"
          color={overallInf > 0 ? BAD : overallInf < 0 ? GOOD : GOLD_LT} Icon={ArrowTrendingLines24Regular} />
        <KPI label="Declared savings" value={fmtCompact(totalDeclared, cur)} sub="recorded at close-out" color={GOOD} Icon={Money24Regular} />
        <KPI label="Savings implied by rates" value={fmtCompact(totalDerived, cur)} sub="benchmark minus awarded" color={GOOD} Icon={Money24Regular} />
      </div>

      <Panel title="Briefing" sub="Generated from every close-out record in the selected period — refreshed each time a tender is closed out.">
        <InsightList insights={insights} />
      </Panel>
    </>
  );
}

// ── Tab 2: Awarded rates by category & year ───────────────────────────────────

function AwardedRatesTab({ records }) {
  const { years, rows } = useMemo(() => awardedRatesByCategoryYear(records), [records]);
  const [metric, setMetric] = useState("median");

  if (!rows.length) return <Panel><Empty>No awarded rates recorded yet.</Empty></Panel>;

  const metricLabel = { median: "Median", mean: "Average", max: "Highest", min: "Lowest" }[metric];

  const series = rows.slice(0, 8).map((r, i) => ({
    label: r.category,
    color: SERIES_COLORS[i % SERIES_COLORS.length],
    xLabel: (x) => String(x),
    points: years
      .filter((y) => r.cells[y])
      .map((y) => ({
        x: Number(y),
        y: r.cells[y][metric],
        tip: `${y}: ${fmtMoney(r.cells[y][metric], r.cells[y].currency)} (${r.cells[y].n} award${r.cells[y].n === 1 ? "" : "s"})`,
      })),
  })).filter((s) => s.points.length);

  return (
    <>
      <Panel
        title="Awarded rates by category and year"
        sub={`${metricLabel} awarded rate per category. Median is the default because a single outsized award should not shift the picture.`}
        right={
          <Field>
            <Select value={metric} onChange={(_, d) => setMetric(d.value)} style={{ minWidth: 130 }}>
              <option value="median">Median rate</option>
              <option value="mean">Average rate</option>
              <option value="min">Lowest rate</option>
              <option value="max">Highest rate</option>
            </Select>
          </Field>
        }
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr style={{ background: NAVY }}>
                <th style={th("left")}>CATEGORY</th>
                {years.map((y) => <th key={y} style={th("right")}>{y}</th>)}
                <th style={th("right")}>AWARDS</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.category} style={{ borderBottom: "1px solid #EEF0F4", background: i % 2 ? "#FAFBFC" : "#fff" }}>
                  <td style={td("left", { fontWeight: 600, color: NAVY })}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: SERIES_COLORS[i % SERIES_COLORS.length], flexShrink: 0 }} />
                      {r.category}
                    </div>
                  </td>
                  {years.map((y) => {
                    const c = r.cells[y];
                    if (!c) return <td key={y} style={td("right", { color: "#CCC" })}>—</td>;
                    return (
                      <td key={y} style={td("right")}>
                        <Tooltip
                          relationship="label"
                          content={`${c.n} award${c.n === 1 ? "" : "s"} · range ${fmtMoney(c.min, c.currency)} – ${fmtMoney(c.max, c.currency)}${c.mixedUnits ? " · mixed units of measure" : ""}${c.mixedCurrency ? " · mixed currencies" : ""}`}
                        >
                          <span>
                            <span style={{ fontWeight: 700, color: GOLD }}>{fmtCompact(c[metric], c.currency)}</span>
                            <span style={{ fontSize: 10, color: MUTED, display: "block" }}>
                              n={c.n}{(c.mixedUnits || c.mixedCurrency) ? " ⚠" : ""}
                            </span>
                          </span>
                        </Tooltip>
                      </td>
                    );
                  })}
                  <td style={td("right", { fontWeight: 700 })}>{r.totalN}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Text style={{ fontSize: 11, color: MUTED, display: "block", marginTop: 10, lineHeight: 1.6 }}>
          ⚠ marks a cell that mixes units of measure or currencies — those figures are a rough guide only.
          For like-for-like comparison use the Price Movement tab, which holds unit and currency constant.
        </Text>
      </Panel>

      {series.length > 0 && (
        <Panel title={`${metricLabel} rate trend`} sub="One line per category, plotted across award years.">
          <LineChart series={series} height={280} yFormat={(v) => fmtCompact(v)} />
        </Panel>
      )}
    </>
  );
}

// ── Tab 3: Inflation ──────────────────────────────────────────────────────────

function heatColor(inf) {
  if (inf === null || inf === undefined) return { bg: "#F7F7F7", color: "#CCC" };
  const clamp = Math.max(-0.3, Math.min(0.3, inf));
  const t = Math.abs(clamp) / 0.3;
  if (clamp < -0.005) return { bg: `rgba(16,124,16,${0.10 + t * 0.42})`, color: t > 0.5 ? "#fff" : "#0A5C0A" };
  if (clamp > 0.005) return { bg: `rgba(196,38,46,${0.10 + t * 0.45})`, color: t > 0.5 ? "#fff" : "#8B1A20" };
  return { bg: "#F3F2F1", color: "#666" };
}

function InflationTab({ records }) {
  const { years, rows, overall } = useMemo(() => inflationByCategoryYear(records), [records]);
  if (!rows.length) return <Panel><Empty>Not enough award history to measure inflation yet.</Empty></Panel>;

  const indexSeries = rows.slice(0, 8).map((r, i) => ({
    label: r.category,
    color: SERIES_COLORS[i % SERIES_COLORS.length],
    xLabel: (x) => String(x),
    points: years
      .filter((y) => r.cells[y]?.index !== null && r.cells[y]?.index !== undefined)
      .map((y) => ({
        x: Number(y),
        y: r.cells[y].index,
        tip: `${y}: index ${r.cells[y].index.toFixed(1)}${r.cells[y].inflation !== null ? ` (${fmtSignedPct(r.cells[y].inflation)} on the year)` : " (base year)"}`,
      })),
  })).filter((s) => s.points.length > 1);

  return (
    <>
      <Panel
        title="Price inflation by category and year"
        sub="Measured like-for-like: only commodity lines awarded in both years are compared, so a change in what we bought cannot masquerade as a change in price."
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <thead>
              <tr style={{ background: NAVY }}>
                <th style={th("left")}>CATEGORY</th>
                {years.map((y) => <th key={y} style={th("center")}>{y}</th>)}
                <th style={th("center")}>AVG / YR</th>
                <th style={th("left")}>CONFIDENCE</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.category} style={{ borderBottom: "1px solid #EEF0F4" }}>
                  <td style={td("left", { fontWeight: 600, color: NAVY, background: i % 2 ? "#FAFBFC" : "#fff" })}>{r.category}</td>
                  {years.map((y) => {
                    const c = r.cells[y];
                    if (!c) return <td key={y} style={{ ...td("center"), background: "#F7F7F7", color: "#CCC" }}>—</td>;
                    if (c.base) {
                      return (
                        <td key={y} style={{ ...td("center"), background: "#EEF2F7", color: "#667" }}>
                          <span style={{ fontSize: 10.5, fontWeight: 700 }}>BASE</span>
                          <span style={{ fontSize: 9.5, display: "block", color: MUTED }}>index 100</span>
                        </td>
                      );
                    }
                    const hc = heatColor(c.inflation);
                    return (
                      <td key={y} style={{ ...td("center"), background: hc.bg }}>
                        <Tooltip
                          relationship="label"
                          content={c.matched
                            ? `${c.pairs} like-for-like commodity comparison${c.pairs === 1 ? "" : "s"} · price index ${c.index?.toFixed(1)}`
                            : "No like-for-like line available — this is the shift in median rate and may reflect a change in what was bought."}
                        >
                          <span>
                            <span style={{ fontWeight: 700, color: hc.color, fontSize: 12.5 }}>{fmtSignedPct(c.inflation)}</span>
                            <span style={{ fontSize: 9.5, display: "block", color: hc.color, opacity: 0.8 }}>
                              {c.matched ? `${c.pairs} matched` : "indicative"}
                            </span>
                          </span>
                        </Tooltip>
                      </td>
                    );
                  })}
                  <td style={{ ...td("center"), fontWeight: 800, color: r.avgInflation > 0 ? BAD : r.avgInflation < 0 ? GOOD : "#666", background: i % 2 ? "#FAFBFC" : "#fff" }}>
                    {fmtSignedPct(r.avgInflation)}
                  </td>
                  <td style={{ ...td("left"), background: i % 2 ? "#FAFBFC" : "#fff" }}><ConfidenceChip confidence={r.confidence} /></td>
                </tr>
              ))}
              <tr style={{ background: "#FFF3DC", borderTop: `2px solid ${GOLD}` }}>
                <td style={td("left", { fontWeight: 800, color: GOLD })}>ALL CATEGORIES</td>
                {years.map((y) => (
                  <td key={y} style={td("center", { fontWeight: 800, color: overall[y] > 0 ? BAD : overall[y] < 0 ? GOOD : "#666" })}>
                    {overall[y] === null || overall[y] === undefined ? "—" : fmtSignedPct(overall[y])}
                  </td>
                ))}
                <td style={td("center")} />
                <td style={td("left")} />
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>

      {indexSeries.length > 0 && (
        <Panel title="Price index by category" sub="Each category's first year of data is set to 100. The line shows cumulative price movement since then.">
          <LineChart series={indexSeries} height={280} baseline={100} yFormat={(v) => v.toFixed(0)} />
        </Panel>
      )}

      <Panel title="How to read this" pad="16px 20px">
        <Text style={{ fontSize: 12.5, color: "#555", lineHeight: 1.8, display: "block" }}>
          <strong>Matched</strong> cells compare the same commodity, same unit of measure and same currency across two years — that is a true price change.
          <strong style={{ marginLeft: 8 }}>Indicative</strong> cells had no repeat line to compare, so they show the shift in median rate instead; read them as a signal, not a measurement.
          The <strong>average per year</strong> column is a geometric mean, which is the correct way to average growth rates, and it is the figure the Should-Cost and Budget tools use to escalate today's prices forward.
        </Text>
      </Panel>
    </>
  );
}

// ── Tab 4: Cost savings ───────────────────────────────────────────────────────

function SavingsTab({ records }) {
  const allYears = useMemo(
    () => [...new Set(usableRecords(records).map((r) => r.tenderDate.slice(0, 4)))].sort().reverse(),
    [records],
  );
  const [focusYear, setFocusYear] = useState(() => {
    const cy = String(new Date().getFullYear());
    return allYears.includes(cy) ? cy : (allYears[0] || cy);
  });
  const data = useMemo(() => costSavings(records, focusYear), [records, focusYear]);
  const cur = usableRecords(records)[0]?.currency || "NGN";

  if (!data.byYear.length) return <Panel><Empty>No awards recorded yet.</Empty></Panel>;

  const f = data.focus;

  return (
    <>
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
        <Text style={{ fontSize: 12, color: "#555" }}>Focus year:</Text>
        <Field>
          <Select value={focusYear} onChange={(_, d) => setFocusYear(d.value)} style={{ minWidth: 110 }}>
            {allYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
        </Field>
      </div>

      {f && (
        <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <KPI label={`Declared savings ${focusYear}`} value={fmtCompact(f.declared, cur)} sub={`from ${f.n} close-out${f.n === 1 ? "" : "s"}`} color={GOOD} Icon={Money24Regular} />
          <KPI label="Implied by rates" value={fmtCompact(f.derived, cur)} sub={`across ${f.benchmarked} benchmarked award${f.benchmarked === 1 ? "" : "s"}`} color={GOOD} Icon={Calculator24Regular} />
          <KPI label="Awards beating benchmark" value={f.reductionRate === null ? "—" : fmtPct(f.reductionRate, 0)} sub={`${f.reductions} of ${f.benchmarked}`} color="#0052A3" Icon={CheckmarkCircle24Regular} />
          <KPI label="Avg rate change" value={f.avgPctChange === null ? "—" : `${f.avgPctChange > 0 ? "+" : ""}${f.avgPctChange.toFixed(1)}%`}
            sub="awarded vs benchmark" color={f.avgPctChange > 0 ? BAD : GOOD} Icon={ArrowTrendingLines24Regular} />
        </div>
      )}

      <Panel title="Savings by year" sub="Declared is what buyers recorded at close-out. Implied is what the benchmark and awarded rates actually show. A persistent gap means savings are being under-reported.">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
            <thead>
              <tr style={{ background: NAVY }}>
                <th style={th("left")}>YEAR</th>
                <th style={th("right")}>AWARDS</th>
                <th style={th("right")}>DECLARED SAVINGS</th>
                <th style={th("right")}>IMPLIED BY RATES</th>
                <th style={th("right")}>BEAT BENCHMARK</th>
                <th style={th("right")}>AVG RATE CHANGE</th>
              </tr>
            </thead>
            <tbody>
              {data.byYear.map((y, i) => (
                <tr key={y.year} style={{ borderBottom: "1px solid #EEF0F4", background: y.year === focusYear ? "#FFF8EE" : i % 2 ? "#FAFBFC" : "#fff" }}>
                  <td style={td("left", { fontWeight: 700, color: NAVY })}>
                    {y.year}{y.year === focusYear && <span style={{ marginLeft: 6, fontSize: 9.5, background: `${GOLD}18`, color: GOLD, padding: "1px 7px", borderRadius: 8, fontWeight: 700 }}>FOCUS</span>}
                  </td>
                  <td style={td("right")}>{y.n}</td>
                  <td style={td("right", { fontWeight: 700, color: y.declared > 0 ? GOOD : "#BBB" })}>{y.declared > 0 ? fmtCompact(y.declared, y.currency) : "—"}</td>
                  <td style={td("right", { fontWeight: 700, color: y.derived > 0 ? GOOD : "#BBB" })}>{y.derived > 0 ? fmtCompact(y.derived, y.currency) : "—"}</td>
                  <td style={td("right")}>{y.reductionRate === null ? "—" : `${fmtPct(y.reductionRate, 0)} (${y.reductions}/${y.benchmarked})`}</td>
                  <td style={td("right", { fontWeight: 700, color: y.avgPctChange === null ? "#BBB" : y.avgPctChange > 0 ? BAD : GOOD })}>
                    {y.avgPctChange === null ? "—" : `${y.avgPctChange > 0 ? "+" : ""}${y.avgPctChange.toFixed(1)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title={`Savings by category — ${focusYear}`} sub="Where the value was delivered.">
        {!data.byCategory.length ? <Empty>No awards in {focusYear}.</Empty> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(() => {
              const max = Math.max(...data.byCategory.map((c) => Math.max(c.declared, c.derived)), 1);
              return data.byCategory.map((c) => (
                <div key={c.category}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, gap: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: NAVY }}>{c.category}</span>
                    <span style={{ fontSize: 11.5, color: MUTED }}>
                      {c.n} award{c.n === 1 ? "" : "s"}
                      {c.avgPctChange !== null && (
                        <span style={{ marginLeft: 8, color: c.avgPctChange > 0 ? BAD : GOOD, fontWeight: 700 }}>
                          {c.avgPctChange > 0 ? "+" : ""}{c.avgPctChange.toFixed(1)}% vs benchmark
                        </span>
                      )}
                    </span>
                  </div>
                  {[["Declared", c.declared, GOOD], ["Implied", c.derived, "#4AA84A"]].map(([label, val, color]) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 10, color: MUTED, width: 54, flexShrink: 0 }}>{label}</span>
                      <div style={{ flex: 1, height: 11, background: "#F3F4F6", borderRadius: 6, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(val / max) * 100}%`, background: color, borderRadius: 6 }} />
                      </div>
                      <span style={{ fontSize: 11, color: INK, fontWeight: 600, minWidth: 80, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {val > 0 ? fmtCompact(val, cur) : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        )}
      </Panel>
    </>
  );
}

// ── Tab 5: Price movement ─────────────────────────────────────────────────────

function PriceMovementTab({ records }) {
  const lines = useMemo(() => trackableCommodities(records, 2), [records]);
  const [key, setKey] = useState(() => lines[0]?.key || "");
  const movement = useMemo(() => (key ? priceMovement(records, key) : null), [records, key]);

  if (!lines.length) {
    return (
      <Panel>
        <Empty>
          No commodity has been awarded twice yet, so there is no price history to plot.<br />
          Movement tracking begins once the same commodity group and unit of measure is closed out a second time.
        </Empty>
      </Panel>
    );
  }

  const m = movement;
  const line = m?.line;

  return (
    <>
      <Panel title="Historical price movement" sub="Award-by-award history for a single commodity line, holding category, unit of measure and currency constant."
        right={
          <div style={{ minWidth: 320, maxWidth: 440, width: "100%" }}>
            <SearchableSelect
              value={key}
              onChange={(v) => { if (v) setKey(v); }}
              placeholder="Search commodities…"
              emptyText="No commodity matches that search."
              options={lines.map((l) => ({
                value: l.key,
                label: l.label,
                meta: `${l.n} awards`,
                sub: [l.category, l.currency, l.unitOfMeasure].filter(Boolean).join(" · "),
              }))}
            />
          </div>
        }
      >
        {!m ? <Empty>Select a commodity.</Empty> : (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
              <KPI label="Latest awarded rate" value={fmtCompact(line.latestRate, line.currency)} sub={line.latestAward.tenderDate} Icon={Money24Regular} />
              <KPI label="Median across history" value={fmtCompact(line.median, line.currency)} sub={`range ${fmtCompact(line.min)} – ${fmtCompact(line.max)}`} color="#0052A3" Icon={DataBarVertical24Regular} />
              <KPI label="Trend" value={m.trendPctPerYear === null ? "—" : fmtSignedPct(m.trendPctPerYear)}
                sub={m.fit ? `fitted over ${line.n} awards, r²=${m.fit.r2.toFixed(2)}` : "insufficient history"}
                color={m.trendPctPerYear > 0 ? BAD : GOOD} Icon={ArrowTrendingLines24Regular} />
              <KPI label="Volatility" value={line.volatility === null ? "—" : `±${fmtPct(line.volatility)}`}
                sub={line.volatility > 0.35 ? "wide spread — negotiable" : line.volatility < 0.12 ? "tight — little price room" : "moderate spread"}
                color={line.volatility > 0.35 ? WARN : GOOD} Icon={ArrowTrendingLines24Regular} />
            </div>

            <LineChart
              height={290}
              yFormat={(v) => fmtCompact(v)}
              series={[{
                label: line.label,
                color: GOLD_LT,
                xLabel: (x) => new Date(x).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
                points: m.series.map((p) => ({
                  x: p.t,
                  y: p.rate,
                  tip: `${new Date(p.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}\n${fmtMoney(p.rate, line.currency)}\n${p.supplier || "supplier not recorded"}${p.changeFromPrev !== null ? `\n${fmtSignedPct(p.changeFromPrev)} on previous award` : ""}`,
                })),
              }]}
            />

            <div style={{ overflowX: "auto", marginTop: 18 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
                <thead>
                  <tr style={{ background: NAVY }}>
                    <th style={th("left")}>TENDER DATE</th>
                    <th style={th("left")}>REFERENCE</th>
                    <th style={th("left")}>SUPPLIER</th>
                    <th style={th("right")}>AWARDED RATE</th>
                    <th style={th("right")}>VS PREVIOUS</th>
                  </tr>
                </thead>
                <tbody>
                  {[...m.series].reverse().map((p, i) => (
                    <tr key={p.tenderRef + i} style={{ borderBottom: "1px solid #EEF0F4", background: i % 2 ? "#FAFBFC" : "#fff" }}>
                      <td style={td("left")}>{new Date(p.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                      <td style={td("left", { fontWeight: 600, color: NAVY })}>{p.tenderRef}</td>
                      <td style={td("left")}>{p.supplier || "—"}</td>
                      <td style={td("right", { fontWeight: 700, color: GOLD })}>{fmtMoney(p.rate, line.currency)}</td>
                      <td style={td("right", { fontWeight: 700, color: p.changeFromPrev === null ? "#BBB" : p.changeFromPrev > 0 ? BAD : GOOD })}>
                        {p.changeFromPrev === null ? "first award" : fmtSignedPct(p.changeFromPrev)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {line.suppliers.length > 1 && (
              <Text style={{ fontSize: 12, color: "#555", display: "block", marginTop: 14, lineHeight: 1.7 }}>
                <strong>{line.suppliers.length} suppliers</strong> have won this line: {line.suppliers.join(", ")}.
                Competitive tension exists — use it.
              </Text>
            )}
            {line.suppliers.length === 1 && (
              <div style={{ marginTop: 14, background: SEVERITY.warn.bg, border: `1px solid ${SEVERITY.warn.border}`, borderRadius: 8, padding: "11px 14px" }}>
                <Text style={{ fontSize: 12, color: "#6B4A00", lineHeight: 1.7 }}>
                  Every award on this line has gone to <strong>{line.suppliers[0]}</strong>. Without a competing bid we cannot validate the rate — widen the bidder list at next renewal.
                </Text>
              </div>
            )}
          </>
        )}
      </Panel>
    </>
  );
}

// ── Tab 6: Should-cost & negotiation ──────────────────────────────────────────

function ShouldCostTab({ records }) {
  const catalogue = useMemo(() => productCatalogue(records), [records]);

  const [q, setQ] = useState({
    category: "", commodityGroup: "", unitOfMeasure: "", currency: "NGN",
    location: "", targetDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().slice(0, 10),
    quotedRate: "",
  });

  // Options are scoped to what has actually been bought, so an impossible
  // combination cannot be assembled in the first place.
  const groupsInCat = catalogue[q.category] || [];
  const selectedProduct = groupsInCat.find((g) => g.group === q.commodityGroup) || null;
  const unitOptions = selectedProduct
    ? selectedProduct.units
    : [...new Set(groupsInCat.flatMap((g) => g.units))].sort();
  const currencyOptions = selectedProduct
    ? selectedProduct.currencies
    : [...new Set(groupsInCat.flatMap((g) => g.currencies))].sort();
  const locationOptions = selectedProduct
    ? selectedProduct.locations
    : [...new Set(groupsInCat.flatMap((g) => g.locations))].sort();

  function set(k, v) {
    setQ((p) => {
      const next = { ...p, [k]: v };

      // Changing category invalidates the product beneath it.
      if (k === "category") {
        next.commodityGroup = "";
        next.unitOfMeasure = "";
        next.location = "";
        const only = (catalogue[v] || []).flatMap((g) => g.currencies);
        if (only.length && !only.includes(next.currency)) next.currency = only[0];
      }

      // Choosing a product pins the unit and currency it is actually bought in.
      if (k === "commodityGroup") {
        const prod = (catalogue[next.category] || []).find((g) => g.group === v);
        if (prod) {
          next.unitOfMeasure = prod.units.length === 1 ? prod.units[0] : "";
          if (!prod.currencies.includes(next.currency)) next.currency = prod.currencies[0];
          if (next.location && !prod.locations.includes(next.location)) next.location = "";
        } else {
          next.unitOfMeasure = "";
        }
      }
      return next;
    });
  }

  const quoted = parseFloat(q.quotedRate) || 0;
  const result = useMemo(() => {
    if (!q.category) return null;
    return quoted > 0 ? assessRate(records, q, quoted) : { available: false, shouldCost: shouldCost(records, q) };
  }, [records, q, quoted]);

  const sc = result?.shouldCost;
  const toneColor = { good: GOOD, neutral: "#0052A3", warn: WARN, bad: BAD };

  return (
    <>
      <Panel
        title="What should this cost?"
        sub="Name the product you are about to tender. The estimate is built only from awards of that same product, on the same unit of measure and in the same currency — never from other products in the category."
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
          <Field label="Category" required>
            <Select value={q.category} onChange={(_, d) => set("category", d.value)}>
              <option value="">— Select category —</option>
              {BENCHMARK_CATEGORIES.map((c) => (
                <option key={c} value={c} disabled={!catalogue[c]}>
                  {c}{catalogue[c] ? ` (${catalogue[c].length})` : " — no history"}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Commodity group"
            hint={q.category
              ? `${groupsInCat.length} product${groupsInCat.length === 1 ? "" : "s"} with history — type to search`
              : "Pick a category first"}
          >
            <SearchableSelect
              value={q.commodityGroup}
              onChange={(v) => set("commodityGroup", v)}
              disabled={!q.category || groupsInCat.length === 0}
              placeholder={q.category ? "Search products…" : "Select a category first"}
              clearLabel="— Whole category (not product-specific) —"
              emptyText="No product in this category matches that search."
              options={groupsInCat.map((g) => ({
                value: g.group,
                label: g.group,
                meta: `${g.n} award${g.n === 1 ? "" : "s"}`,
                sub: [
                  g.units.join(" / "),
                  g.currencies.join(" / "),
                  g.lastDate ? `last ${new Date(g.lastDate).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}` : "",
                ].filter(Boolean).join(" · "),
              }))}
            />
          </Field>

          <Field
            label="Unit of measure"
            hint={selectedProduct && selectedProduct.units.length === 1 ? "Set from award history" : undefined}
          >
            <Select
              value={q.unitOfMeasure}
              onChange={(_, d) => set("unitOfMeasure", d.value)}
              disabled={!q.category || unitOptions.length === 0}
            >
              <option value="">— Any —</option>
              {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
            </Select>
          </Field>

          <Field
            label="Currency"
            hint={selectedProduct && selectedProduct.currencies.length === 1 ? "Set from award history" : undefined}
          >
            <Select value={q.currency} onChange={(_, d) => set("currency", d.value)}>
              {(currencyOptions.length ? currencyOptions : ["NGN", "USD", "EUR"]).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </Field>

          <Field label="Location">
            <Select
              value={q.location}
              onChange={(_, d) => set("location", d.value)}
              disabled={locationOptions.length === 0}
            >
              <option value="">— Any —</option>
              {locationOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </Select>
          </Field>

          <Field label="Expected award date">
            <Input type="date" value={q.targetDate} onChange={(_, d) => set("targetDate", d.value)} />
          </Field>

          <Field label="Quoted rate (optional)" hint="Enter a supplier's price to score it">
            <Input type="number" value={q.quotedRate} onChange={(_, d) => set("quotedRate", d.value)} placeholder="0.00" />
          </Field>
        </div>

        {q.category && !q.commodityGroup && groupsInCat.length > 0 && (
          <div style={{ marginTop: 14, display: "flex", gap: 10, background: SEVERITY.warn.bg, border: `1px solid ${SEVERITY.warn.border}`, borderRadius: 9, padding: "11px 15px" }}>
            <Warning24Regular style={{ color: WARN, fontSize: 17, flexShrink: 0, marginTop: 1 }} />
            <Text style={{ fontSize: 12.5, color: "#6B4A00", lineHeight: 1.65 }}>
              No commodity group selected, so this will blend every product in {q.category} —
              {" "}{groupsInCat.slice(0, 3).map((g) => g.group).join(", ")}{groupsInCat.length > 3 ? ` and ${groupsInCat.length - 3} more` : ""} —
              which are priced on different units and are not comparable. Pick a product for a real estimate.
            </Text>
          </div>
        )}
      </Panel>

      {!q.category && <Panel><Empty>Select a category to run the estimate.</Empty></Panel>}

      {q.category && sc && !sc.available && (
        <Panel>
          <div style={{ display: "flex", gap: 12, background: SEVERITY.warn.bg, border: `1px solid ${SEVERITY.warn.border}`, borderRadius: 10, padding: "14px 16px" }}>
            <Warning24Regular style={{ color: WARN, fontSize: 18, flexShrink: 0, marginTop: 1 }} />
            <div>
              <Text style={{ fontSize: 13, color: "#5A4200", lineHeight: 1.7, display: "block" }}>{sc.reason}</Text>
              {(sc.suggestCurrency || sc.suggestUnit || sc.suggestCategory) && (
                <div style={{ display: "flex", gap: 8, marginTop: 11, flexWrap: "wrap" }}>
                  {sc.suggestCurrency && (
                    <Button size="small" appearance="primary" style={{ background: GOLD }}
                      onClick={() => setQ((p) => ({ ...p, currency: sc.suggestCurrency }))}>
                      Switch to {sc.suggestCurrency}
                    </Button>
                  )}
                  {sc.suggestUnit && (
                    <Button size="small" appearance="primary" style={{ background: GOLD }}
                      onClick={() => setQ((p) => ({ ...p, unitOfMeasure: sc.suggestUnit }))}>
                      Use "{sc.suggestUnit}"
                    </Button>
                  )}
                  {sc.suggestCategory && (
                    <Button size="small" appearance="primary" style={{ background: GOLD }}
                      onClick={() => setQ((p) => ({ ...p, category: sc.suggestCategory }))}>
                      Switch to {sc.suggestCategory}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </Panel>
      )}

      {q.category && sc && sc.available && (
        <>
          <Panel
            title="Fair value range"
            sub={`Built from ${sc.n} award${sc.n === 1 ? "" : "s"} of ${sc.productScoped ? "this exact product" : "this category"} · ${sc.matchLevel}`}
            right={
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{
                  background: sc.productScoped ? "#EAF6EA" : "#FFF4EC",
                  color: sc.productScoped ? GOOD : WARN,
                  padding: "2px 10px", borderRadius: 10, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap",
                }}>
                  {sc.productScoped ? "Product-specific" : "Category blend"}
                </span>
                <ConfidenceChip confidence={sc.confidence} showNote />
              </div>
            }
          >
            {sc.warning && (
              <div style={{ display: "flex", gap: 10, background: SEVERITY.warn.bg, border: `1px solid ${SEVERITY.warn.border}`, borderRadius: 9, padding: "11px 15px", marginBottom: 16 }}>
                <Warning24Regular style={{ color: WARN, fontSize: 17, flexShrink: 0, marginTop: 1 }} />
                <Text style={{ fontSize: 12.5, color: "#6B4A00", lineHeight: 1.65 }}>{sc.warning}</Text>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
              {[
                { label: "Stretch target", value: sc.estimate.low, note: "lower quartile — an excellent outcome", color: GOOD },
                { label: "Target rate", value: sc.estimate.target, note: "market median — aim here", color: GOLD },
                { label: "Walk-away", value: sc.estimate.high, note: "upper quartile — challenge above this", color: WARN },
                { label: "Ceiling", value: sc.estimate.ceiling, note: "highest ever awarded", color: BAD },
              ].map((b) => (
                <div key={b.label} style={{ background: `${b.color}0D`, border: `1.5px solid ${b.color}33`, borderRadius: 10, padding: "13px 15px" }}>
                  <Text style={{ fontSize: 10, fontWeight: 700, color: b.color, letterSpacing: "0.5px", display: "block", marginBottom: 5 }}>
                    {b.label.toUpperCase()}
                  </Text>
                  <Text style={{ fontSize: 16, fontWeight: 800, color: NAVY, display: "block", fontVariantNumeric: "tabular-nums" }}>
                    {fmtMoney(b.value, sc.currency)}
                  </Text>
                  <Text style={{ fontSize: 10.5, color: MUTED, display: "block", marginTop: 3, lineHeight: 1.5 }}>{b.note}</Text>
                </div>
              ))}
            </div>

            <div style={{ background: "#F8F9FC", borderRadius: 10, padding: "13px 16px", marginBottom: sc.locationNote ? 10 : 0 }}>
              <Text style={{ fontSize: 12, color: "#555", lineHeight: 1.75, display: "block" }}>
                <strong>Escalation applied:</strong> the most recent comparable award was on{" "}
                {new Date(sc.escalation.fromDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} at {fmtMoney(sc.base.latestRate, sc.currency)}.
                Rates have been carried forward {sc.escalation.yearsOut.toFixed(2)} year{sc.escalation.yearsOut === 1 ? "" : "s"} to your award date at{" "}
                <strong style={{ color: sc.escalation.rate > 0 ? BAD : GOOD }}>{fmtSignedPct(sc.escalation.rate)} a year</strong>{" "}
                (a factor of {sc.escalation.factor.toFixed(3)}), the measured inflation rate for {q.category}.
              </Text>
            </div>
            {sc.locationNote && (
              <Text style={{ fontSize: 12, color: "#555", lineHeight: 1.7, display: "block" }}>📍 {sc.locationNote}</Text>
            )}
          </Panel>

          {result?.available && (
            <Panel title="Assessment of the quoted rate" right={
              <span style={{ background: `${toneColor[result.tone]}18`, color: toneColor[result.tone], padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 800 }}>
                {result.verdict.toUpperCase()}
              </span>
            }>
              <div style={{ background: `${toneColor[result.tone]}0D`, border: `1.5px solid ${toneColor[result.tone]}33`, borderRadius: 10, padding: "15px 18px", marginBottom: 16 }}>
                <Text style={{ fontSize: 14, fontWeight: 700, color: NAVY, display: "block", marginBottom: 6 }}>
                  {fmtMoney(result.quotedRate, sc.currency)} — {result.headline}
                </Text>
                <Text style={{ fontSize: 12.5, color: "#555", lineHeight: 1.7 }}>
                  Sits at the <strong>{result.percentileRank === null ? "—" : `${result.percentileRank.toFixed(0)}th percentile`}</strong> of comparable awards.
                  {result.savingVsTarget > 0
                    ? ` Negotiating to the target rate is already achieved — this quote is ${fmtMoney(result.savingVsTarget, sc.currency)} per unit below it.`
                    : ` Closing to the target rate would save ${fmtMoney(Math.abs(result.savingVsTarget), sc.currency)} per unit.`}
                </Text>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
                <div style={{ background: "#F8F9FC", borderRadius: 9, padding: "12px 14px" }}>
                  <Text style={{ fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>OPEN AT</Text>
                  <Text style={{ fontSize: 14, fontWeight: 800, color: GOOD }}>{fmtMoney(result.negotiation.stretch, sc.currency)}</Text>
                </div>
                <div style={{ background: "#F8F9FC", borderRadius: 9, padding: "12px 14px" }}>
                  <Text style={{ fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>SETTLE AT</Text>
                  <Text style={{ fontSize: 14, fontWeight: 800, color: GOLD }}>{fmtMoney(result.negotiation.target, sc.currency)}</Text>
                </div>
                <div style={{ background: "#F8F9FC", borderRadius: 9, padding: "12px 14px" }}>
                  <Text style={{ fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>ESCALATE ABOVE</Text>
                  <Text style={{ fontSize: 14, fontWeight: 800, color: WARN }}>{fmtMoney(result.negotiation.walkAway, sc.currency)}</Text>
                </div>
              </div>

              {result.negotiation.levers.length > 0 && (
                <>
                  <Text style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: "0.5px", display: "block", marginBottom: 9 }}>
                    NEGOTIATION LEVERS
                  </Text>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {result.negotiation.levers.map((l, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, background: "#FFFBF2", border: "1px solid #FFE8B8", borderRadius: 8, padding: "10px 14px" }}>
                        <Lightbulb24Regular style={{ color: GOLD, fontSize: 16, flexShrink: 0, marginTop: 1 }} />
                        <Text style={{ fontSize: 12.5, color: "#5A4200", lineHeight: 1.65 }}>{l}</Text>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Panel>
          )}

          <Panel title="Comparable awards used" sub="The evidence behind the estimate — most recent first.">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                <thead>
                  <tr style={{ background: NAVY }}>
                    <th style={th("left")}>TENDER DATE</th>
                    <th style={th("left")}>REFERENCE</th>
                    <th style={th("left")}>COMMODITY</th>
                    <th style={th("left")}>SUPPLIER</th>
                    <th style={th("left")}>LOCATION</th>
                    <th style={th("right")}>AWARDED RATE</th>
                  </tr>
                </thead>
                <tbody>
                  {sc.basis.map((r, i) => (
                    <tr key={r.id || i} style={{ borderBottom: "1px solid #EEF0F4", background: i % 2 ? "#FAFBFC" : "#fff" }}>
                      <td style={td("left")}>{new Date(r.tenderDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                      <td style={td("left", { fontWeight: 600, color: NAVY })}>{r.tenderRef}</td>
                      <td style={td("left")}>{r.commodityGroup || "—"}{r.unitOfMeasure ? ` · ${r.unitOfMeasure}` : ""}</td>
                      <td style={td("left")}>{r.supplierName || "—"}</td>
                      <td style={td("left")}>{r.location || "—"}</td>
                      <td style={td("right", { fontWeight: 700, color: GOLD })}>{fmtMoney(r.awardedRate, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sc.n > sc.basis.length && (
              <Text style={{ fontSize: 11.5, color: MUTED, display: "block", marginTop: 10 }}>
                Showing the 12 most recent of {sc.n} comparable awards. All {sc.n} are used in the calculation.
              </Text>
            )}
          </Panel>
        </>
      )}
    </>
  );
}

// ── Tab 7: Suppliers ──────────────────────────────────────────────────────────

function SuppliersTab({ records }) {
  const rows = useMemo(() => supplierScorecard(records), [records]);
  if (!rows.length) return <Panel><Empty>No supplier awards recorded yet.</Empty></Panel>;
  const cur = usableRecords(records)[0]?.currency || "NGN";

  return (
    <Panel
      title="Supplier competitiveness"
      sub="Price index compares each supplier's awarded rates against the median rate on the same commodity lines. 100 is exactly at market; below 100 means they consistently price cheaper."
    >
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
          <thead>
            <tr style={{ background: NAVY }}>
              <th style={th("left")}>SUPPLIER</th>
              <th style={th("right")}>AWARDS</th>
              <th style={th("right")}>LINES</th>
              <th style={th("left")}>CATEGORIES</th>
              <th style={th("center")}>PRICE INDEX</th>
              <th style={th("right")}>VS BENCHMARK</th>
              <th style={th("right")}>DECLARED SAVINGS</th>
              <th style={th("left")}>LATEST AWARD</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s, i) => {
              const idx = s.priceIndex;
              const idxColor = idx === null ? "#BBB" : idx < 95 ? GOOD : idx > 105 ? BAD : "#666";
              return (
                <tr key={s.supplier} style={{ borderBottom: "1px solid #EEF0F4", background: i % 2 ? "#FAFBFC" : "#fff" }}>
                  <td style={td("left", { fontWeight: 700, color: NAVY })}>{s.supplier}</td>
                  <td style={td("right", { fontWeight: 700 })}>{s.awards}</td>
                  <td style={td("right")}>{s.lineCount}</td>
                  <td style={td("left", { fontSize: 11, color: "#555" })}>
                    {s.categories.slice(0, 2).join(", ")}{s.categories.length > 2 ? ` +${s.categories.length - 2}` : ""}
                  </td>
                  <td style={td("center")}>
                    {idx === null ? <span style={{ color: "#BBB" }}>—</span> : (
                      <Tooltip relationship="label" content={`Based on ${s.comparable} award${s.comparable === 1 ? "" : "s"} that had a comparable line median`}>
                        <span style={{ background: `${idxColor}18`, color: idxColor, padding: "3px 11px", borderRadius: 10, fontWeight: 800, fontSize: 12 }}>
                          {idx.toFixed(0)}
                        </span>
                      </Tooltip>
                    )}
                  </td>
                  <td style={td("right", { fontWeight: 700, color: s.avgPctChange === null ? "#BBB" : s.avgPctChange > 0 ? BAD : GOOD })}>
                    {s.avgPctChange === null ? "—" : `${s.avgPctChange > 0 ? "+" : ""}${s.avgPctChange.toFixed(1)}%`}
                  </td>
                  <td style={td("right", { color: s.declaredSavings > 0 ? GOOD : "#BBB", fontWeight: 600 })}>
                    {s.declaredSavings > 0 ? fmtCompact(s.declaredSavings, cur) : "—"}
                  </td>
                  <td style={td("left", { fontSize: 11.5, color: "#555" })}>
                    {s.latestAward ? new Date(s.latestAward).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Text style={{ fontSize: 11.5, color: MUTED, display: "block", marginTop: 12, lineHeight: 1.65 }}>
        A supplier with a single award has no meaningful index — their own rate is the line median. Read the index alongside the awards and lines columns.
      </Text>
    </Panel>
  );
}

// ── Tab 8: Budget ─────────────────────────────────────────────────────────────

function BudgetTab({ records }) {
  const nextYear = new Date().getFullYear() + 1;
  const [targetYear, setTargetYear] = useState(String(nextYear));
  const proj = useMemo(() => budgetProjection(records, targetYear), [records, targetYear]);

  if (!proj.available) return <Panel><Empty>Not enough award history to project a budget yet.</Empty></Panel>;

  return (
    <Panel
      title={`Rate projection for ${targetYear}`}
      sub={`Applies each category's measured inflation to its ${proj.latestYear} median award. Use these figures to set budget rates and evaluate whether a supplier's proposed escalation is reasonable.`}
      right={
        <Field>
          <Select value={targetYear} onChange={(_, d) => setTargetYear(d.value)} style={{ minWidth: 110 }}>
            {[0, 1, 2, 3].map((k) => {
              const y = String(Number(proj.latestYear) + k);
              return <option key={y} value={y}>{y}</option>;
            })}
          </Select>
        </Field>
      }
    >
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <thead>
            <tr style={{ background: NAVY }}>
              <th style={th("left")}>CATEGORY</th>
              <th style={th("right")}>BASIS</th>
              <th style={th("right")}>CURRENT MEDIAN</th>
              <th style={th("center")}>INFLATION / YR</th>
              <th style={th("center")}>UPLIFT</th>
              <th style={th("right")}>BUDGET LOW</th>
              <th style={th("right")}>BUDGET RATE</th>
              <th style={th("right")}>BUDGET HIGH</th>
              <th style={th("left")}>CONFIDENCE</th>
            </tr>
          </thead>
          <tbody>
            {proj.rows.map((r, i) => (
              <tr key={r.category} style={{ borderBottom: "1px solid #EEF0F4", background: i % 2 ? "#FAFBFC" : "#fff" }}>
                <td style={td("left", { fontWeight: 700, color: NAVY })}>{r.category}</td>
                <td style={td("right", { fontSize: 11, color: MUTED })}>{r.n} · {r.basisYear}</td>
                <td style={td("right")}>{fmtCompact(r.currentMedian, r.currency)}</td>
                <td style={td("center", { fontWeight: 700, color: r.inflationRate > 0 ? BAD : r.inflationRate < 0 ? GOOD : "#666" })}>
                  {fmtSignedPct(r.inflationRate)}
                </td>
                <td style={td("center", { fontWeight: 700, color: r.uplift > 0 ? BAD : r.uplift < 0 ? GOOD : "#666" })}>
                  {fmtSignedPct(r.uplift)}
                </td>
                <td style={td("right", { color: "#666" })}>{fmtCompact(r.projectedLow, r.currency)}</td>
                <td style={td("right", { fontWeight: 800, color: GOLD, fontSize: 13 })}>{fmtCompact(r.projectedMedian, r.currency)}</td>
                <td style={td("right", { color: "#666" })}>{fmtCompact(r.projectedHigh, r.currency)}</td>
                <td style={td("left")}><ConfidenceChip confidence={r.confidence} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 14, background: "#F8F9FC", borderRadius: 9, padding: "13px 16px" }}>
        <Text style={{ fontSize: 12, color: "#555", lineHeight: 1.75 }}>
          <strong>Budget rate</strong> is the projected median — the figure to budget against.
          <strong style={{ marginLeft: 6 }}>Low</strong> and <strong>high</strong> are the projected quartiles and describe the realistic spread,
          so they set the tolerance band for a budget variance. Where confidence is Fair or Low the projection rests on few awards;
          hold a larger contingency on those lines until more close-outs land.
        </Text>
      </div>
    </Panel>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

const TABS = [
  { id: "insights",  label: "Briefing",       Icon: Lightbulb24Regular,          Comp: InsightsTab },
  { id: "products",  label: "Products",       Icon: Box24Regular,                Comp: ProductIntelligenceTab },
  { id: "rates",     label: "Awarded Rates",  Icon: DataBarVertical24Regular,    Comp: AwardedRatesTab },
  { id: "inflation", label: "Inflation",      Icon: ArrowTrendingLines24Regular, Comp: InflationTab },
  { id: "savings",   label: "Cost Savings",   Icon: Money24Regular,              Comp: SavingsTab },
  { id: "movement",  label: "Price Movement", Icon: History24Regular,            Comp: PriceMovementTab },
  { id: "shouldcost",label: "Should-Cost",    Icon: Calculator24Regular,         Comp: ShouldCostTab },
  { id: "suppliers", label: "Suppliers",      Icon: People24Regular,             Comp: SuppliersTab },
  { id: "budget",    label: "Budget",         Icon: Calculator24Regular,         Comp: BudgetTab },
];

export default function MarketIntelligencePage() {
  const { records, loading, isDemoMode, refresh } = useBenchmarkDB();
  const navigate = useNavigate();
  const [tab, setTab] = useState("insights");
  const [range, setRange] = useState({ preset: "all", from: "", to: "" });

  const Active = TABS.find((t) => t.id === tab)?.Comp || InsightsTab;

  const bounds = useMemo(() => dateBounds(records), [records]);
  const years = useMemo(() => availableYears(records), [records]);
  const presets = useMemo(() => buildPresets(years), [years]);

  // Every tab reads from this, so the period control governs the whole dashboard.
  const scoped = useMemo(
    () => filterByDateRange(records, range.from, range.to),
    [records, range.from, range.to],
  );

  const usable = usableRecords(records);
  const usableScoped = usableRecords(scoped);

  // Inflation needs two award years inside the window to say anything.
  const yearsInRange = useMemo(() => availableYears(scoped), [scoped]);
  const narrowWindow = range.preset !== "all" && yearsInRange.length < 2;

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto", padding: "0 2px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Tooltip content="Back to database" relationship="label">
            <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate("/benchmark")} style={{ color: GOLD_LT }} />
          </Tooltip>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg,${GOLD},${GOLD_LT})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ArrowTrendingLines24Regular style={{ color: "#fff", fontSize: 20 }} />
          </div>
          <div>
            <Text size={600} weight="bold" style={{ color: NAVY, lineHeight: 1.2, display: "block" }}>Market Intelligence</Text>
            <Text style={{ fontSize: 12, color: MUTED }}>
              Price history and trends to guide sourcing, budgeting, cost validation and negotiation
            </Text>
          </div>
          {isDemoMode && (
            <span style={{ fontSize: 11, fontWeight: 700, color: "#E65C00", background: "#FFF4EC", border: "1px solid #FFD6A8", padding: "2px 10px", borderRadius: 20 }}>Demo Mode</span>
          )}
        </div>
        <Tooltip content="Refresh" relationship="label">
          <Button appearance="subtle" icon={<ArrowSync24Regular />} onClick={refresh} />
        </Tooltip>
      </div>

      <div style={{ borderBottom: "1px solid #E4E8EF", marginBottom: 20, display: "flex", overflowX: "auto" }}>
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "10px 16px", border: "none", background: "none",
                borderBottom: active ? `2px solid ${GOLD_LT}` : "2px solid transparent",
                color: active ? GOLD : MUTED, fontWeight: active ? 700 : 400,
                fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
                fontFamily: "inherit",
              }}
            >
              <t.Icon style={{ fontSize: 16 }} />
              {t.label}
            </button>
          );
        })}
      </div>

      {!loading && usable.length > 0 && (
        <DateRangeControl
          presets={presets}
          value={range}
          onChange={setRange}
          bounds={bounds}
          resultCount={usableScoped.length}
          totalCount={usable.length}
        />
      )}

      {!loading && usable.length > 0 && narrowWindow && (
        <div style={{ display: "flex", gap: 10, background: SEVERITY.info.bg, border: `1px solid ${SEVERITY.info.border}`, borderRadius: 9, padding: "11px 15px", marginBottom: 16 }}>
          <Info24Regular style={{ color: "#0052A3", fontSize: 17, flexShrink: 0, marginTop: 1 }} />
          <Text style={{ fontSize: 12.5, color: "#334", lineHeight: 1.65 }}>
            This window covers {yearsInRange.length === 1 ? `only ${yearsInRange[0]}` : "less than one award year"}, so
            year-on-year inflation cannot be computed inside it. Product-level price movement, rates and savings still work
            normally — widen the period to compare across years.
          </Text>
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
          <Spinner size="large" label="Loading price intelligence..." />
        </div>
      ) : usable.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", padding: "60px 40px", textAlign: "center" }}>
          <ArrowTrendingLines24Regular style={{ fontSize: 48, color: "#D0D7E5", marginBottom: 12 }} />
          <Text size={500} weight="bold" style={{ color: NAVY, display: "block", marginBottom: 8 }}>No price history yet</Text>
          <Text style={{ color: MUTED, display: "block", marginBottom: 20, maxWidth: 520, marginLeft: "auto", marginRight: "auto", lineHeight: 1.7 }}>
            Market intelligence is built from tender close-out records. Each close-out that captures an awarded rate and tender date
            adds to the evidence base — the estimates sharpen with every one.
          </Text>
          <Button appearance="primary" onClick={() => navigate("/benchmark")} style={{ background: GOLD_LT }}>
            Go to Benchmark Database
          </Button>
        </div>
      ) : usableScoped.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", padding: "50px 40px", textAlign: "center" }}>
          <Warning24Regular style={{ fontSize: 42, color: "#D0D7E5", marginBottom: 12 }} />
          <Text size={500} weight="bold" style={{ color: NAVY, display: "block", marginBottom: 8 }}>No awards in this period</Text>
          <Text style={{ color: MUTED, display: "block", marginBottom: 18 }}>
            {usable.length} award{usable.length === 1 ? "" : "s"} exist outside the selected date range.
          </Text>
          <Button appearance="secondary" onClick={() => setRange({ preset: "all", from: "", to: "" })}>
            Show all time
          </Button>
        </div>
      ) : (
        <Active records={scoped} />
      )}
    </div>
  );
}
